"use client";

import {
  Application,
  EmploymentType,
  Lead,
  MaximumBudget,
} from "@justhome/common/api/core";
import {
  Dispatch,
  FC,
  PropsWithChildren,
  Reducer,
  createContext,
  useReducer,
} from "react";
import { DeepPartial } from "react-hook-form";

export enum BudgetCheckStepName {
  federalState,
  employmentType,
  employedIncome,
  selfEmployedIncome,
  retiredIncome,
  personalDetails,
  equity,
  contact,
  result,
}

export interface BudgetCheckFormValues {
  federalState: MaximumBudget["federalState"];
  employmentType: EmploymentType;
  employmentStart?: string;
  monthlyNetIncome?: number;
  equity: number;
  existingDebt?: number;
  yearlyNetIncome?: number;
}

export type YesNo = boolean;

export interface FormState<T> {
  values: DeepPartial<T>[];
  currentStep: number;
  history: number[];
}

export interface BudgetCheckProcessState {
  lead?: Lead;
  application?: Application;
  maximumBudget?: MaximumBudget;
  budgetCheckForm?: BudgetCheckFormValues;
  budgetCheckFormState: FormState<BudgetCheckFormValues>;
}

export type Actions =
  | {
      type: "SET_BUDGET_CHECK_FORM";
      form: BudgetCheckFormValues;
    }
  | {
      type: "SET_BUDGET_CHECK_FORM_STATE";
      form: FormState<BudgetCheckFormValues>;
    }
  | {
      type: "SET_MAXIMUM_BUDGET";
      maximumBudget: MaximumBudget;
    }
  | {
      type: "SET_LEAD";
      lead: Lead;
    };

const defaultState: BudgetCheckProcessState = {
  budgetCheckFormState: {
    values: [],
    history: [],
    currentStep: 0,
  },
};

export const reducer: Reducer<BudgetCheckProcessState, Actions> = (
  state,
  action,
) => {
  switch (action.type) {
    case "SET_BUDGET_CHECK_FORM":
      return { ...state, budgetCheckForm: action.form };
    case "SET_BUDGET_CHECK_FORM_STATE":
      return { ...state, budgetCheckFormState: action.form };
    case "SET_MAXIMUM_BUDGET":
      return { ...state, maximumBudget: action.maximumBudget };
    case "SET_LEAD":
      return {
        ...state,
        lead: action.lead,
      };
    default:
      return state;
  }
};

export interface BudgetCheckContextValue {
  state: BudgetCheckProcessState;
  dispatch: Dispatch<Actions>;
}

export const BudgetCheckContext = createContext<BudgetCheckContextValue>({
  state: defaultState,
  dispatch: () => {},
});

BudgetCheckContext.displayName = "Application";

export const BudgetCheckProvider: FC<
  PropsWithChildren<{ initialState?: BudgetCheckProcessState }>
> = ({ children, initialState }) => {
  const [state, dispatch] = useReducer(reducer, initialState || defaultState);

  return (
    <BudgetCheckContext.Provider value={{ state, dispatch }}>
      {children}
    </BudgetCheckContext.Provider>
  );
};
